import { useEffect } from 'react';

/**
 * A React hook to set the document page title.
 *
 * This hook updates `document.title` when the component mounts or when the
 * `title` or `options` change. If `title` is `undefined`, it defaults to `'Ethereal'`.
 *
 * @param title - The main part of the title. If undefined, falls back to `'Ethereal'`.
 * @param options - Optional configuration for the title behavior.
 * @param options.suffix - Optional suffix appended to the title (default: `' | Ethereal'`).
 * @param options.preserveOnUnmount - If true, the title will not revert when the component unmounts.
 */
export function usePageTitle(
  title: string | undefined,
  options?: {
    suffix?: string;
    preserveOnUnmount?: boolean;
  }
) {
  const { suffix = ' | Ethereal', preserveOnUnmount = false } = options || {};

  const fullTitle = title ? `${title}${suffix}` : 'Ethereal';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = fullTitle;

    return () => {
      if (!preserveOnUnmount) {
        document.title = previousTitle;
      }
    };
  }, [fullTitle, preserveOnUnmount]);
}
